import { InterestMethods } from '../Configuration/GeneralOptions/OptionConstants';

/**
 * Calculate interest on an overdue invoice balance.
 *
 * @param balance      - outstanding balance (positive number)
 * @param dueDate      - invoice due date
 * @param asOfDate     - date to calculate interest through
 * @param rate         - interest rate (percentage for simple methods, dollar amount for flat monthly)
 * @param method       - one of InterestMethods values
 * @param gracePeriod  - number of days after due date before interest starts accruing
 * @returns interest amount (always >= 0)
 */
export function calculateInterest(
    balance: number,
    dueDate: Date,
    asOfDate: Date,
    rate: number,
    method: string,
    gracePeriod: number,
): number {
    if (rate <= 0 || balance <= 0) return 0;

    const msPerDay = 86_400_000;
    const totalDaysOverdue = Math.floor((asOfDate.getTime() - dueDate.getTime()) / msPerDay);
    const daysOverdue = totalDaysOverdue - (gracePeriod || 0);

    if (daysOverdue <= 0) return 0;

    switch (method) {
        case InterestMethods.SIMPLE_DAILY:
            // Monthly rate applied daily: balance * (rate/100) / 30 * daysOverdue
            return balance * (rate / 100) / 30 * daysOverdue;

        case InterestMethods.SIMPLE_MONTHLY: {
            // Round to nearest whole month
            const months = Math.round(daysOverdue / 30);
            if (months <= 0) return 0;
            return balance * (rate / 100) * months;
        }

        case InterestMethods.FLAT_MONTHLY: {
            // Rate is a flat dollar amount per month; charge for each month (ceil)
            const months = Math.ceil(daysOverdue / 30);
            if (months <= 0) return 0;
            return rate * months;
        }

        default:
            return 0;
    }
}

export default calculateInterest;
